import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { skipGlobalLoadingHttpOptions } from 'src/app/core/common-services/skip-global-loading.context';
import { BaseApiService } from 'src/app/shared/services/base-api.service';
import { GeneralResponse } from 'src/app/shared/shared-data-models';
import {
  AdminDashboardPageRow,
  DEFAULT_SYSTEM_PAGE_ACTIONS,
  UpdateSystemPagePayload,
} from '../../data-models/permissions-dashboard.models';

@Injectable({
  providedIn: 'root',
})
export class MenuDataService {
  private controllerUrl: string = `${environment.appConfig.adminGatewayServiceBaseUrl}/api/dashboard/DashAuthentication`;

  /** Reuses one in-flight HTTP call when getUserMenu() is subscribed multiple times before completion. */
  private userMenuInFlight: Observable<any> | null = null;

  constructor(private baseApi: BaseApiService<any>) {}

  getUserMenu(): Observable<any> {
    if (!this.userMenuInFlight) {
      this.userMenuInFlight = this.baseApi
        .Get(`${this.controllerUrl}/user-pages`)
        .pipe(
          shareReplay({ bufferSize: 1, refCount: true }),
          finalize(() => {
            this.userMenuInFlight = null;
          })
        );
    }
    return this.userMenuInFlight;
  }

  updateSystemPage(
    id: string,
    body: UpdateSystemPagePayload
  ): Observable<GeneralResponse<boolean>> {
    const safeId = encodeURIComponent(id);
    return this.baseApi.Put(
      `${this.controllerUrl}/system-pages/${safeId}`,
      body
    );
  }

  /** Same page definitions as the admin menu (AdminPageDetails); full catalog, not filtered by user. */
  getAdminDashboardPagesCatalog(
    skipGlobalLoading = false
  ): Observable<GeneralResponse<AdminDashboardPageRow[]>> {
    const opts = skipGlobalLoading ? skipGlobalLoadingHttpOptions() : undefined;
    return this.baseApi
      .Get(`${this.controllerUrl}/admin-dashboard-pages`, opts)
      .pipe(
        map((res: any) => {
          const rawList = res?.resource ?? res?.data ?? [];
          const list = Array.isArray(rawList)
            ? rawList.map((r: any) => this.normalizeAdminPageRow(r))
            : [];
          return {
            ...res,
            resource: list,
            data: list,
          } as GeneralResponse<AdminDashboardPageRow[]>;
        })
      );
  }

  private normalizeAdminPageRow(raw: any): AdminDashboardPageRow {
    const actionsRaw = raw?.actions ?? raw?.Actions;
    const actions = Array.isArray(actionsRaw)
      ? actionsRaw.map((a: unknown) => String(a))
      : [];
    const actionsResolved =
      actions.length > 0 ? actions : [...DEFAULT_SYSTEM_PAGE_ACTIONS];
    return {
      id: raw?.id ?? raw?.Id ?? '',
      name: raw?.name ?? raw?.Name ?? '',
      nameAr: raw?.nameAr ?? raw?.NameAr,
      category: raw?.category ?? raw?.Category ?? '',
      categoryAr: raw?.categoryAr ?? raw?.CategoryAr,
      route: raw?.route ?? raw?.Route ?? '',
      icon: raw?.icon ?? raw?.Icon,
      childrens: raw?.childrens ?? raw?.Childrens ?? raw?.children,
      requiredPermissions:
        raw?.requiredPermissions ?? raw?.RequiredPermissions,
      requiredRoles: raw?.requiredRoles ?? raw?.RequiredRoles,
      actions: actionsResolved,
      actionsLabel: actionsResolved.join(', '),
    };
  }

}
